import type { NextRequest } from "next/server";
import { NextResponse } from "next/server";

import { resolveDefaultLocale } from "./autodetectLocale";
import {
  buildLocalePath,
  getDefaultLangForCountry,
  isNonLocalizedPath,
  isValidLocalePath,
  LOCALE_COOKIE,
  LOCALE_HEADER,
  parseLocalePath,
} from "./locales";

// const ONE_YEAR = 60 * 60 * 24 * 365;
const ONE_YEAR = 31536000;
export const AUTH_USER_TOKEN = "auth_token";
export const STAFF_BENEFIT_TOKEN = "auth_staff_benefit_token";
const TAB_SESSION_COOKIE = "tab_session_id";
const TAB_SESSION_HEADER = "x-tab-session-id";

function ensureCookie(res: NextResponse, cookieName: string, existing?: string | null) {
  if (existing) {
    const prod = process.env.NODE_ENV === "production";
    res.cookies.set(cookieName, existing, {
      httpOnly: true,
      sameSite: "lax",
      path: "/",
      maxAge: ONE_YEAR,
      secure: prod,
    });
  }
}

function ensureTabSession(
  req: NextRequest,
  requestHeaders: Headers,
): { sessionId: string | null; isNew: boolean } {
  // First try to get from cookies (if backend set it)
  let sessionId = req.cookies.get(TAB_SESSION_COOKIE)?.value ?? null;

  if (!sessionId) {
    const headerSessionId = req.headers.get(TAB_SESSION_HEADER);
    if (headerSessionId) {
      sessionId = headerSessionId;
      // console.log(`[middleware] Found tabSessionId in header: ${sessionId.substring(0, 20)}...`);
    } else {
      // Debug: Log when header is missing for RSC requests
      const url = req.url;
      if (url.includes("_rsc") || url.includes("__rsc__")) {
        // console.log(`[middleware] RSC request but no ${TAB_SESSION_HEADER} header found`);
      }
    }
  }

  // Forward session ID to server components via request headers
  // This ensures server components can read it via headers().get("x-tab-session-id")
  if (sessionId) {
    requestHeaders.set(TAB_SESSION_HEADER, sessionId);
  }

  return { sessionId, isNew: false };
}

export function middleware(req: NextRequest) {
  const requestHeaders = new Headers(req.headers);
  const { sessionId, isNew: _isNew } = ensureTabSession(req, requestHeaders);
  const res = NextResponse.next({
    request: {
      headers: requestHeaders,
    },
  });

  // Also set in response headers so server components can access it
  if (sessionId) {
    res.headers.set(TAB_SESSION_HEADER, sessionId);
  }
  const authCookieVal = req.cookies.get(AUTH_USER_TOKEN)?.value ?? null;
  ensureCookie(res, AUTH_USER_TOKEN, authCookieVal);

  const authStaffBenefitCookieVal = req.cookies.get(STAFF_BENEFIT_TOKEN)?.value ?? null;
  ensureCookie(res, STAFF_BENEFIT_TOKEN, authStaffBenefitCookieVal);

  // Removed shared locale/currency cookies - now using tab-specific session preferences only

  // No longer set tab_session_id here - backend token API creates it via Set-Cookie
  // Middleware only forwards existing session ID in x-tab-session-id header

  return res;
}

// Matcher for the middleware to run on all paths except Next.js internals
export const matcher = ["/((?!_next).*)"];

// ────────────────────────────────────────────────────────────────────────────
// Locale routing — validate the two root slugs (/{country}/{lang}) and pass
// through. A redirect is the EXCEPTION, used only at the base path or when the
// slugs are present but unsupported. No blanket 308; internal links already
// carry the prefix, so the normal job is validate + pass-through.
// ────────────────────────────────────────────────────────────────────────────

// LOCALE_HEADER is defined in ./locales (dependency-free single source) and is
// re-exported from the package root via index.ts. It is imported above for use here.

/** Returns a response when locale routing applies; null to let the app middleware continue. */
export function localeMiddleware(req: NextRequest): NextResponse | null {
  const { pathname } = req.nextUrl;
  // Assets + API routes (incl. /api-site) are never localized → skip locale routing entirely.
  if (isNonLocalizedPath(pathname)) return null;

  const [, seg1, seg2] = pathname.split("/");

  // 1) Base path → redirect to the default country/lang (cookie → Fastly country → defaults)
  if (!seg1) {
    const code = resolveDefaultLocale(req);
    return temporaryRedirect(req, buildLocalePath(code));
  }

  // 2) Both slugs valid & supported → pass through (NO redirect)
  if (isValidLocalePath(seg1, seg2)) return null;

  // 3) First slug is a supported country but lang missing/invalid → complete with country default
  const langForCountry = getDefaultLangForCountry(seg1);
  if (langForCountry) {
    const rest = pathname.slice(`/${seg1}`.length);
    return temporaryRedirect(req, `/${seg1.toLowerCase()}/${langForCountry}${rest}`);
  }

  // 4) No valid slugs (typo / unknown) → redirect to the resolved default, preserving the path
  const code = resolveDefaultLocale(req);
  return temporaryRedirect(req, `${buildLocalePath(code)}${pathname}`);
}

/**
 * 307 to a locale path, preserving the app's `basePath`.
 *
 * MUST build the target from `req.nextUrl.clone()`, never `new URL(path, req.url)`.
 * `req.nextUrl` is a NextURL: it keeps `basePath` *out* of `.pathname` and re-adds it
 * when the URL is serialized, so a clone round-trips the prefix. A plain `URL` has no
 * such knowledge and silently drops it — with basePath `/fly` that turned
 * `/fly` into a 307 to `/cl/es`, which 404s, breaking every entry through `/`.
 * `pathname` here is therefore always basePath-relative, which is also why the
 * locale parsing above needs no `/fly` handling of its own.
 */
function temporaryRedirect(req: NextRequest, toPath: string): NextResponse {
  const url = req.nextUrl.clone();
  url.pathname = toPath;
  url.search = req.nextUrl.search;
  const res = NextResponse.redirect(url, 307);
  res.headers.set("Cache-Control", "no-store"); // target depends on cookie/geo — never cache
  return res;
}

/** For requests that already have valid slugs: forward x-locale + remember the choice. */
export function withLocaleHeaders(req: NextRequest, requestHeaders: Headers, res: NextResponse) {
  const { code } = parseLocalePath(req.nextUrl.pathname);
  if (!code) return;
  requestHeaders.set(LOCALE_HEADER, code);
  res.headers.set(LOCALE_HEADER, code);
  res.cookies.set(LOCALE_COOKIE, code, { path: "/", maxAge: ONE_YEAR, sameSite: "lax" });
}
